import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import styles from './FulfillmentFulfillForm.module.scss';
import Button from '../../common/Button';
import { merge, isEqual } from 'lodash';
import OrderShipmentForm from '../OrderShipmentForm';
import OrderRejectionForm from '../OrderRejectionForm';
import { v4 as uuidv4 } from 'uuid';
import shipmentsIcon from './shipments-icon.svg';
import rejectionsIcon from './rejections-icon.svg';
import {
  FULFILLMENT_STATE_NEW,
  FULFILLMENT_STATE_SEND_DOCUMENTS,
  FULFILLMENT_STATE_SENT_TO_SUPPLIER,
  FULFILLMENT_STATE_COMPLETED,
  FULFILLMENT_STATE_REJECTED,
  FULFILLMENT_STATE_CANCELED,
  FULFILLMENT_STATE_CANCELLING,
  FULFILLMENT_STATE_CSR,
  FULFILLMENT_STATE_WAITING_FOR_SHIPPING,
  FULFILLMENT_STATE_VERIFY_TRACKING_NUMBER,
} from '../../../constants/orderStates';
import { canFulfill } from '../../../utils/fulfillmentHelper';

function calculateInitialQuantities(allQuantities, previousShipments, previousRejections) {
  const initialQuantities = Object.keys(allQuantities).reduce((quantities, id) => {
    quantities[id] = 0;
    return quantities;
  }, {});

  if (previousShipments) {
    previousShipments.forEach((shipment) => {
      for (const fulfillmentLineId in shipment.quantities) {
        initialQuantities[fulfillmentLineId] += shipment.quantities[fulfillmentLineId];
      }
    });
  }

  if (previousRejections) {
    previousRejections.forEach((rejection) => {
      for (const fulfillmentLineId in rejection.quantities) {
        initialQuantities[fulfillmentLineId] += rejection.quantities[fulfillmentLineId];
      }
    });
  }

  return initialQuantities;
}

function FulfillmentFulfillForm(props) {
  const { fulfillment, onUpdate, isFullyFulfilled, previousShipments, previousRejections, singleFulfillment } = props;

  const fulfillmentId = fulfillment.id;

  const [shipments, setShipments] = useState([]);
  const [rejection, setRejection] = useState(null);

  const allQuantities = useMemo(() => {
    const quantities = {};
    fulfillment.fulfillment_lines.forEach((fulfillmentLine) => {
      quantities[fulfillmentLine.id] = fulfillmentLine.original_quantity || fulfillmentLine.quantity;
    });
    return quantities;
  }, [fulfillment]);

  const initialQuantities = calculateInitialQuantities(allQuantities, previousShipments, previousRejections);
  const [quantities, setQuantities] = useState(initialQuantities);

  const hasRemainingQuantity = useMemo(() => {
    for (const fulfillmentLineId in quantities) {
      if (quantities[fulfillmentLineId] < allQuantities[fulfillmentLineId]) {
        return true;
      }
    }

    return false;
  }, [allQuantities, quantities]);

  const calculateQuantities = useCallback(() => {
    const currentQuantities = { ...initialQuantities };

    for (const shipment of shipments) {
      for (const fulfillmentLineId in shipment.quantities) {
        currentQuantities[fulfillmentLineId] += shipment.quantities[fulfillmentLineId];
      }
    }

    if (rejection) {
      for (const fulfillmentLineId in rejection.quantities) {
        currentQuantities[fulfillmentLineId] += rejection.quantities[fulfillmentLineId];
      }
    }

    return currentQuantities;
  }, [initialQuantities, shipments, rejection]);

  const addShipment = useCallback(() => {
    const newQuantities = {};
    for (const fulfillmentLineId in allQuantities) {
      newQuantities[fulfillmentLineId] = allQuantities[fulfillmentLineId] - quantities[fulfillmentLineId];
    }

    const newShipment = {
      quantities: newQuantities,
      trackingNumber: '',
      shippingMethod: fulfillment.shipping_method_code,
    };

    setShipments([...shipments, newShipment]);
  }, [allQuantities, quantities, shipments, fulfillment.shipping_method_code]);

  useEffect(() => {
    if (!shipments.length && hasRemainingQuantity) {
      addShipment();
    }
  }, [hasRemainingQuantity, addShipment, shipments]);

  useEffect(() => {
    const newQuantities = calculateQuantities();
    if (!isEqual(quantities, newQuantities)) {
      setQuantities(newQuantities);
    }
  }, [calculateQuantities, quantities]);

  useEffect(() => {
    onUpdate(fulfillmentId, { shipments, rejection });
  }, [onUpdate, fulfillmentId, shipments, rejection, quantities]);

  function addRejection() {
    const rejectionQuantities = {};
    for (const fulfillmentLineId in allQuantities) {
      rejectionQuantities[fulfillmentLineId] = allQuantities[fulfillmentLineId] - quantities[fulfillmentLineId];
    }

    setRejection({
      quantities: rejectionQuantities,
      rejectId: uuidv4(),
    });
  }

  function removeShipment(index) {
    const newShipments = [...shipments];
    newShipments.splice(index, 1);
    setShipments(newShipments);
  }

  function updateShipments(index, value) {
    const newShipments = [...shipments];
    newShipments[index] = merge({}, newShipments[index], value);
    setShipments(newShipments);
    calculateQuantities();
  }

  function removeRejection() {
    setRejection(null);
  }

  function updateRejection(value) {
    setRejection(merge({}, rejection, value));
    calculateQuantities();
  }

  function isBeforeFulfill() {
    return [
      FULFILLMENT_STATE_NEW,
      FULFILLMENT_STATE_SEND_DOCUMENTS,
      FULFILLMENT_STATE_SENT_TO_SUPPLIER,
      FULFILLMENT_STATE_WAITING_FOR_SHIPPING,
      FULFILLMENT_STATE_VERIFY_TRACKING_NUMBER,
    ].includes(state);
  }

  function isAfterFulfill() {
    return [
      FULFILLMENT_STATE_COMPLETED,
      FULFILLMENT_STATE_REJECTED,
      FULFILLMENT_STATE_CANCELED,
      FULFILLMENT_STATE_CANCELLING,
    ].includes(state);
  }

  function isCsrFulfill() {
    return state === FULFILLMENT_STATE_CSR;
  }

  const maxQuantities = {};
  Object.keys(allQuantities).forEach((fulfillmentLineId) => {
    maxQuantities[fulfillmentLineId] = allQuantities[fulfillmentLineId] - initialQuantities[fulfillmentLineId];
  });

  const hasPreviousData = previousShipments.length || previousRejections.length;
  const { state } = fulfillment.currentState;
  const canFulfillFulfillment = canFulfill(fulfillment);

  return (
    <article className={cn(styles.fulfillmentFulfillForm, singleFulfillment ? styles.singleFulfillment : '')}>
      {!singleFulfillment && <h2>Fulfillment #{fulfillmentId}:</h2>}

      {canFulfillFulfillment ? (
        <>
          {hasPreviousData ? (
            <section className={styles.previousData}>
              <section className={styles.shipmentsSection}>
                <h3>
                  <img src={shipmentsIcon} alt="Shipments Icon" />
                  Previous Shipments
                </h3>
                {previousShipments.length ? (
                  <div className={styles.shipments}>
                    {previousShipments.map((shipment, i) => (
                      <div className={styles.shipmentWrapper} key={i} data-testid={`previous-shipments-${i}`}>
                        <OrderShipmentForm fulfillment={fulfillment} shipment={shipment} readonly />
                      </div>
                    ))}
                  </div>
                ) : (
                  <h4>No Previous Shipments</h4>
                )}
              </section>

              <section className={styles.rejectionSection}>
                <h3>
                  <img src={rejectionsIcon} alt="Cancellations Icon" />
                  Previous Cancellations
                </h3>
                {previousRejections.length ? (
                  <div className={styles.rejections}>
                    {previousRejections.map((rejection, i) => (
                      <div className={styles.rejectionWrapper} key={i} data-testid={`previous-rejections-${i}`}>
                        <OrderRejectionForm fulfillment={fulfillment} rejection={rejection} readonly />
                      </div>
                    ))}
                  </div>
                ) : (
                  <h4>No Previous Cancellations</h4>
                )}
              </section>
            </section>
          ) : null}

          {hasPreviousData && !isFullyFulfilled ? <hr className={styles.previousSeparator} /> : null}

          {shipments.length ? (
            <section className={styles.shipmentsSection}>
              <h3>
                <img src={shipmentsIcon} alt="Shipments Icon" />
                New Shipments
              </h3>
              <div className={styles.shipments}>
                {shipments.map((shipment, i) => (
                  <div className={styles.shipmentWrapper} key={i} data-testid={`shipments-${i}`}>
                    {shipments.length > 1 && (
                      <span className={cn(styles.removeButton, 'ic-close')} onClick={() => removeShipment(i)} />
                    )}

                    <OrderShipmentForm
                      fulfillment={fulfillment}
                      shipment={shipment}
                      maxQuantities={maxQuantities}
                      onChange={(value) => updateShipments(i, value)}
                    />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {rejection ? (
            <section className={styles.rejectionSection} data-testid={`rejections`}>
              <h3>
                <img src={rejectionsIcon} alt="Cancellations Icon" />
                New Cancellations
              </h3>

              <div className={styles.rejectionWrapper} data-testid="rejection">
                <span className={cn(styles.removeButton, 'ic-close')} onClick={() => removeRejection()} />

                <OrderRejectionForm
                  fulfillment={fulfillment}
                  rejection={rejection}
                  maxQuantities={maxQuantities}
                  onChange={(value) => updateRejection(value)}
                />
              </div>
            </section>
          ) : null}

          {!isFullyFulfilled && (
            <>
              <hr />

              <section className={styles.actions}>
                <Button
                  text="Add Shipment"
                  onClick={() => addShipment()}
                  disabled={!hasRemainingQuantity}
                  title={
                    hasRemainingQuantity
                      ? undefined
                      : 'You cannot add another shipment because already selected to fulfill everything'
                  }
                  testId="add-shipment-button"
                />

                {!rejection && (
                  <Button
                    text="Add Cancellation"
                    onClick={() => addRejection()}
                    disabled={!hasRemainingQuantity}
                    title={
                      hasRemainingQuantity
                        ? undefined
                        : 'You cannot cancel because you already selected to fulfill everything'
                    }
                    testId="add-rejection-button"
                  />
                )}
              </section>
            </>
          )}
        </>
      ) : (
        <p className={styles.cantFulfill} data-testid="cant-fulfill">
          {isBeforeFulfill() && 'This order is in process.'}
          {isAfterFulfill() && 'This order is complete.'}
          {isCsrFulfill() && "This order can't be edited at this point."}
        </p>
      )}
    </article>
  );
}

FulfillmentFulfillForm.defaultProps = {
  isFullyFulfilled: false,
  previousShipments: [],
  previousRejections: [],
};

FulfillmentFulfillForm.propTypes = {
  fulfillment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fulfillment_lines: PropTypes.array.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  isFullyFulfilled: PropTypes.bool,
  previousShipments: PropTypes.array,
  previousRejections: PropTypes.array,
};

export default FulfillmentFulfillForm;
